<template>
  <div class="py-12" style="background-color: #f9fffb;">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div class="mb-8 text-center">
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">บทความและความรู้</h2>
        <p class="text-gray-600">
          ติดตามข่าวสารใหม่ล่าสุด และความรู้ที่เป็นประโยชน์
        </p>
      </div>

      <!-- Simple Category Filter -->
      <div v-if="!isLoading && !error && availableCategories.length > 1" class="mb-6">
        <div class="flex flex-wrap justify-center gap-2">
          <button 
            @click="selectedCategory = 'all'"
            :class="selectedCategory === 'all' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            ทั้งหมด
          </button>
          <button 
            v-for="category in availableCategories.slice(0, 5)" 
            :key="category"
            @click="selectedCategory = category"
            :class="selectedCategory === category ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            {{ category }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600 mx-auto mb-2"></div>
          <p class="text-sm text-gray-500">กำลังโหลด...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-gray-500 mb-3">{{ error }}</p>
        <button
          @click="fetchArticles"
          class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          ลองใหม่
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!paginatedArticles.length" class="text-center py-12">
        <p class="text-gray-500">ยังไม่มีบทความในระบบ</p>
      </div>

      <!-- Articles Grid -->
      <div v-if="!isLoading && !error && paginatedArticles.length > 0">
        <!-- Articles -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div
            v-for="article in paginatedArticles"
            :key="article._id"
            class="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors shadow-sm"
          >
            <!-- Article Image -->
            <div class="relative h-40 bg-gray-100">
              <img
                v-if="article.mainImage"
                :src="article.mainImage"
                :alt="article.title"
                class="w-full h-full object-cover"
                @error="handleImageError($event)"
              />
              <div v-else class="flex items-center justify-center h-full">
                <i class="fas fa-newspaper text-xl text-gray-400"></i>
              </div>
              
              <!-- Category Badge -->
              <div class="absolute top-3 left-3">
                <span class="text-xs text-gray-600 bg-white bg-opacity-90 px-2 py-1 rounded">
                  {{ article.category }}
                </span>
              </div>
            </div>
            
            <!-- Article Content -->
            <div class="p-4">
              <h3 class="font-medium text-gray-900 mb-2 line-clamp-2">
                {{ article.title || 'ไม่มีชื่อ' }}
              </h3>
              <p class="text-sm text-gray-600 mb-3 line-clamp-2">
                {{ article.excerpt || 'ไม่มีคำอธิบาย' }}
              </p>
              
              <!-- Article Meta -->
              <div class="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>{{ article.author || 'ไม่ระบุ' }}</span>
                <span>{{ (article.views || 0).toLocaleString() }} การอ่าน</span>
              </div>
              
              <div class="flex items-center justify-between">
                <span class="text-xs text-gray-500">
                  {{ formatDate(article.publishDate || article.createdAt) }}
                </span>
                <button
                  @click="openArticle(article)"
                  class="text-sm text-gray-700 hover:text-gray-900"
                >
                  อ่านต่อ →
                </button>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useNuxtApp } from '#app';

// Props
const props = defineProps({
  clusterId: {
    type: String,
    required: true
  },
  showLoadMore: {
    type: Boolean,
    default: false
  },
  maxArticles: {
    type: Number,
    default: 9
  }
});

const { $DSS } = useNuxtApp();

// Data states
const articles = ref([]);
const isLoading = ref(true);
const error = ref(null);

// Filter states
const selectedCategory = ref('all');



// Latest articles - แสดงแค่ 9 รายการล่าสุด
const filteredArticles = computed(() => {
  if (!Array.isArray(articles.value)) {
    return []
  }

  // กรองเฉพาะบทความที่เผยแพร่แล้ว
  let publishedArticles = articles.value
    .filter(article => article.status === 'published')

  // กรองตามหมวดหมู่ถ้ามีการเลือก
  if (selectedCategory.value !== 'all') {
    publishedArticles = publishedArticles.filter(article => article.category === selectedCategory.value)
  }

  // เรียงตาม createdAt จากล่าสุดไปเก่าสุด
  publishedArticles = publishedArticles.sort((a, b) => {
    const dateA = new Date(a.createdAt || a.publishDate || 0)
    const dateB = new Date(b.createdAt || b.publishDate || 0)
    return dateB - dateA // จากล่าสุดไปเก่าสุด
  })

  // แสดงแค่ 9 รายการแรก
  return publishedArticles.slice(0, 9)
})

// Available categories from articles data
const availableCategories = computed(() => {
  if (!Array.isArray(articles.value)) {
    return []
  }
  
  const categories = [...new Set(
    articles.value
      .filter(article => article.status === 'published' && article.category)
      .map(article => article.category)
  )]
  return categories.sort()
})

// แสดงบทความล่าสุด 9 รายการ (ไม่ต้อง pagination)
const paginatedArticles = computed(() => {
  return filteredArticles.value
})

// API Functions
const fetchArticles = async () => {
  if (!props.clusterId) {
    error.value = 'ไม่พบรหัสคลัสเตอร์';
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = null;
  
  try {
    console.log('Fetching articles for cluster:', props.clusterId);
    
    const response = await $DSS('cms/news', 'GET', null, { clusterId: props.clusterId });
    
    if (response && response.success) {
      let articlesData = response.data || [];
      
      articles.value = articlesData;
      
      console.log('Articles loaded:', articles.value.length);
    } else {
      throw new Error(response?.error || 'Failed to fetch articles');
    }
  } catch (err) {
    console.error('Error fetching articles:', err);
    error.value = err.message || 'ไม่สามารถโหลดบทความได้';
    articles.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Helper functions

const formatDate = (dateString) => {
  if (!dateString) return 'ไม่ระบุ';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch (e) {
    return dateString;
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'published': return 'เผยแพร่';
    case 'draft': return 'ร่าง';
    case 'pending': return 'รอการอนุมัติ';
    default: return 'ไม่ระบุ';
  }
};

const getStatusClass = (status) => {
  switch (status) {
    case 'published': return 'bg-green-100 text-green-800';
    case 'draft': return 'bg-yellow-100 text-yellow-800';
    case 'pending': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const handleImageError = (event) => {
  event.target.style.display = 'none';
  event.target.parentNode.innerHTML = '<div class="flex items-center justify-center h-full"><i class="fas fa-image text-gray-400 text-3xl"></i></div>';
};

const openArticle = (article) => {
  // In a real application, this would navigate to article detail page
  // For now, we'll show an alert or could emit an event
  console.log('Opening article:', article);
  
  // You could emit an event to parent component
  // emit('article-selected', article);
  
  // Or navigate to article page
  // navigateTo(`/articles/${article._id}`);
  
  // For demo purposes, show alert
  alert(`เปิดบทความ: ${article.title}\n\n${article.excerpt || 'ไม่มีคำอธิบาย'}`);
};



// Watch for clusterId changes
watch(() => props.clusterId, (newClusterId) => {
  if (newClusterId) {
    fetchArticles();
  }
}, { immediate: true });



onMounted(() => {
  if (props.clusterId) {
    fetchArticles();
  }
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}
</style>